import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from "react";
import { useSession } from "../../state/SessionProvider";
import { hentUleste, loggUt, minEpost } from "../../state/session";
import { Switch } from "../../components/Switch";
import { jamInfo } from "../../components/jamTekst";
import { PagesPane } from "./PagesPane";
import { AvsluttPane } from "./AvsluttPane";
import { ProduksjonPane } from "./ProduksjonPane";
import { FagfunksjonPane } from "./FagfunksjonPane";
import { StillasPane } from "./StillasPane";
import { krevInnlogging } from "../../state/supabase";
import { FrameratePane } from "./FrameratePane";
import { PRIVAT_ID } from "../../config/production";
import { nyVersjonVenter, paaNyVersjon, taINyVersjon } from "../../state/oppdatering";
import * as loggsynk from "../../state/loggsynk";
import * as rapportsynk from "../../state/rapportsynk";

/** Samme som --t-surface i tokens.css. */
const EXIT_MS = 320;

type Pane = "root" | "sider" | "avslutt" | "produksjon" | "framerate"
  | "fagfunksjon" | "stillas";

/*
 * Tittelen paa tilbakeknappen.
 *
 * Et oppslag og ikke et ternaeruttrykk. Med to undersider var `a ? x : y`
 * riktig; med tre ville den tredje faatt den andres navn, og det er den
 * slags feil som staar lenge foer noen ser den.
 */
const PANE_NAVN: Readonly<Record<Exclude<Pane, "root">, string>> = {
  sider: "Sider",
  avslutt: "Avslutt dagen",
  produksjon: "Produksjon",
  framerate: "Framerate",
  fagfunksjon: "Fagfunksjon og team",
  stillas: "Mens vi bygger",
};

/**
 * Menyen.
 *
 * Glir inn fra høyre og legger seg over hovedfeltet og bunnstripa — men ikke
 * over toppstripa. Regi skal aldri miste klokka, heller ikke i det øyeblikket
 * hen justerer hurtigknapper midt i et skift.
 *
 * Menyen er en flate man arbeider i, ikke en vei til andre flater. Alt som
 * gjøres i den, gjøres inni den, og swipe-rekka forlates aldri.
 * Navigasjon inni menyen skjer horisontalt, i samme retning menyen kom fra.
 */
/** Lar toppstripa lukke menyen med utgangsanimasjonen i behold. */
export interface MenuHandle {
  close(): void;
  reopen(): void;
}

export const Menu = forwardRef<MenuHandle, {
  open: boolean;
  /** Kalles når utgangen starter, ikke når den er ferdig. */
  onClosing(): void;
  onClose(): void;
  onAdjustTimecode(): void;
  onEditButtons(): void;
}>(function Menu({ open, onClosing, onClose, onAdjustTimecode, onEditButtons }, ref) {
  const {
    buttons, pages, showTc, setShowTc, blackout, setBlackout, anchor,
    production, shootDay, unitName, kanJamme, member, glemLokalProduksjon,
  } = useSession();
  const [pane, setPane] = useState<Pane>("root");
  /* Adressen leses naar menyen aapnes. Den endrer seg ikke underveis, og
     et oppslag per aapning er billigere enn en tilstand aa holde i takt. */
  const [epost, setEpost] = useState("");
  const [uleste, setUleste] = useState(0);
  useEffect(() => {
    if (!open) return;
    void (async () => setEpost(await minEpost()))();
    /* PRODUKSJONER, ikke beskjeder. Brukeren: «antall produksjoner du
       har tilgang til som du ikke har vaert inne i enda». To beskjeder
       om samme produksjon er fortsatt ett sted aa gaa. */
    setUleste(new Set(hentUleste().map((u) => u.produksjonId)).size);
  }, [open]);
  /*
   * Ny versjon staar og venter.
   *
   * Leses ved hver aapning OG abonneres paa, fordi den kan melde seg mens
   * menyen alt staar oppe. Se state/oppdatering.ts for hvorfor dette er en
   * rad regi trykker paa og ikke en automatisk omlasting.
   */
  const jam = jamInfo(anchor ?? null, Date.now());
  const [nyVersjon, setNyVersjon] = useState(nyVersjonVenter);
  useEffect(() => paaNyVersjon(() => setNyVersjon(true)), []);

  const [shown, setShown] = useState(false);

  /*
   * UTBOKSEN MAA VAERE SYNLIG.
   *
   * Loggen gaar opp i bakgrunnen, og naar den ikke gjoer det har det til naa
   * ikke staatt noe sted: posten staar paa skjermen, redaktoeren har den
   * ikke, og ingen av dem vet det. `console.error` finnes ikke paa en
   * telefon. Lagt til 25.10.2026, etter at nettopp det skjedde.
   *
   * Leses naar menyen aapnes og paa samme takt som koen toemmes. Null i koen
   * er den normale tilstanden, og da staar det ingenting.
   */
  const [iKoe, setIKoe] = useState(0);
  const [klage, setKlage] = useState("");
  const [nektet, setNektet] = useState(false);
  const [rapporter, setRapporter] = useState(0);
  const [kast, setKast] = useState(false);
  useEffect(() => {
    if (!shown) return;
    let avbrutt = false;
    const les = (): void => {
      void (async () => {
        const [n, r] = await Promise.all([
          loggsynk.ventende(production.id),
          /* RAPPORTKOEN VAR USYNLIG.
             `rapportsynk.ventende` har staatt der med kommentaren «Flata
             viser det» — og ingen flate gjorde det. En rapport som ikke
             gikk opp er verre enn en loggpost: regi har avsluttet dagen
             og tror den er levert. Brukeren 02.11.2026: «Naar jeg
             leverer rapporten for dagen, saa dukker den ikke opp under
             rapporter i arbeidsflaten.» */
          rapportsynk.ventende(production.id),
        ]);
        if (avbrutt) return;
        setIKoe(n);
        setRapporter(r);
        /* Rapportens klage staar foerst naar den finnes: den er den
           dyreste av de to aa gaa glipp av. */
        setKlage(rapportsynk.klagen() !== "" && r > 0
          ? rapportsynk.klagen() : loggsynk.klagen());
        setNektet(loggsynk.avvist());
      })();
    };
    les();
    const id = window.setInterval(les, 4000);
    return () => { avbrutt = true; window.clearInterval(id); };
  }, [shown, production.id]);
  const [dragX, setDragX] = useState<number | null>(null);
  const start = useRef<{ x: number; y: number; axis: "x" | "y" | null } | null>(null);

  useEffect(() => {
    if (!open) { setShown(false); return; }
    setPane("root");
    const t = window.setTimeout(() => setShown(true), 20);
    return () => window.clearTimeout(t);
  }, [open]);

  const exit = useRef(0);
  const close = useCallback((): void => {
    window.clearTimeout(exit.current);
    setShown(false);
    // Toppstripa skal svare med én gang. Venter den på at menyen er
    // avmontert, henger ikonet et kvart sekund etter fingeren.
    onClosing();
    /*
     * Maa matche --t-surface, som .menu bruker paa sin transform.
     * 260 ms tok panelet bort mens det fortsatt var aatti prosent inne, og
     * det poppet ut av skjermen rett foer det landet.
     */
    exit.current = window.setTimeout(onClose, EXIT_MS);
  }, [onClosing, onClose]);

  // Menyknappen i toppstripa skal kunne lukke, ikke bare åpne. Uten dette
  // ville et trykk på den mens menyen står åpen ikke gjøre noe.
  /*
   * Henter menyen tilbake mens den er paa vei ut.
   *
   * Uten dette: trykk paa menyknappen to ganger innen utgangen er ferdig, og
   * App setter menuOpen til true — men den er allerede true, saa React gjoer
   * ingenting og `open` endrer seg aldri. Tidsuret fra foerste trykk fyrte
   * likevel og avmonterte menyen, mens menuShown sto igjen paa true. Da var
   * knappen doed til appen ble startet paa nytt.
   */
  const reopen = useCallback((): void => {
    window.clearTimeout(exit.current);
    setPane("root");
    setShown(true);
  }, []);

  useImperativeHandle(ref, () => ({ close, reopen }), [close, reopen]);

  // Lukkes med swipe mot høyre. På en underside går swipe tilbake til menyen.
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    start.current = { x: e.clientX, y: e.clientY, axis: null };
  };
  /* Med redusert bevegelse skal panelet ikke reise. Den innebygde
     transformen slaar stilarket, saa draget maa sloeyfes her. */
  const rolig = (): boolean =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const s = start.current;
    if (s === null) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (s.axis === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      s.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (s.axis === "y") { start.current = null; return; }
      /*
       * Pekeren fanges foerst her, naar gesten er avgjort et drag.
       *
       * Fanget vi paa pointerdown, ble ogsaa click omdirigert til panelet —
       * og da naadde ingen knapp inne i menyen sitt eget klikk. Hele menyen
       * var doed, inkludert «Lukk loggen og lag rapport». Paa dette punktet
       * er gesten et drag og ikke et trykk, saa det er ingenting aa stjele.
       *
       * Fangsten gir samtidig det den var ment for: slipper fingeren utenfor
       * panelet, kommer pointerup likevel hit, og menyen blir ikke staaende
       * halvveis ute.
       */
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ingen peker */ }
    }
    if (!rolig()) setDragX(Math.max(0, dx));
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    const s = start.current;
    start.current = null;
    const dx = s === null ? 0 : Math.max(0, e.clientX - s.x);
    setDragX(null);
    if (s === null || s.axis !== "x" || dx <= 70) return;
    if (pane !== "root") setPane("root");
    else close();
  };

  if (!open) return null;

  const enabledCount = pages.filter((p) => p.enabled).length;

  return (
    <div className={`overlay${shown ? " overlay--in" : ""}`}>
      <button
        type="button"
        className="overlay__scrim"
        onClick={close}
        aria-label="Lukk menyen"
      />
      <div
        className={`menu${dragX !== null ? " menu--dragging" : ""}`}
        style={dragX !== null ? { transform: `translateX(${dragX}px)` } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {pane === "root" ? (
          <div className="menu__pane">
            <div className="menu__group">
              <span className="menu__groupname">NÅ</span>
              {/* Jam hoerer teknikeren til. Se state/SessionProvider.tsx. */}
              {kanJamme ? (
                <button type="button" className="menu__row" onClick={() => { close(); onAdjustTimecode(); }}>
                  <span>Juster tidskode</span>
                  <span className="menu__rowvalue">
                    {anchor === null ? "" : new Date(anchor.wallUtcMs)
                      .toLocaleTimeString("no-NO", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </button>
              ) : (
                <div className="menu__row">
                  <span>Tidskoden</span>
                  <span className="menu__rowvalue">
                    {anchor === null ? "ikke satt" : `satt ${new Date(anchor.wallUtcMs)
                      .toLocaleTimeString("no-NO", { hour: "2-digit", minute: "2-digit" })}`}
                  </span>
                </div>
              )}
              <button type="button" className="menu__row" onClick={() => setPane("avslutt")}>
                <span style={{ fontWeight: 500 }}>Avslutt dagen</span>
                <span className="menu__rowvalue">›</span>
              </button>
            </div>

            <div className="menu__group">
              <span className="menu__groupname">OPPSETT</span>
              {nyVersjon ? (
                /* Appen bytter aldri versjon av seg selv. Loggen ligger i
                   OPFS og beroeres ikke av at skallet byttes, men oeyeblikket
                   er regis. Se state/oppdatering.ts. */
                <button
                  type="button"
                  className="menu__row"
                  onClick={taINyVersjon}
                >
                  <span style={{ color: "var(--tc)" }}>Ny versjon klar</span>
                  <span className="menu__rowvalue">oppdater</span>
                </button>
              ) : null}
              <button type="button" className="menu__row" onClick={() => setPane("sider")}>
                <span>Sider</span>
                <span className="menu__rowvalue">{enabledCount} av {pages.length}</span>
              </button>
              <button
                type="button"
                className="menu__row"
                onClick={() => { close(); onEditButtons(); }}
              >
                <span>Hurtigknapper</span>
                <span className="menu__rowvalue">{buttons.length} knapper</span>
              </button>
            </div>

            <div className="menu__group">
              <span className="menu__groupname">INNSTILLINGER</span>
              <div className="menu__row">
                <span>Vis tidskode</span>
                <Switch value={showTc} label="Vis tidskode" onChange={() => setShowTc(!showTc)} />
              </div>
              <div className="menu__row">
                <span>Blackout</span>
                <Switch value={blackout} label="Blackout" onChange={() => setBlackout(!blackout)} />
              </div>
            </div>

            {/* Svarer på «hvilken produksjon er jeg på». Det spørsmålet
                stilles oftere enn man tror av en frilanser på tre prosjekter —
                og nå kan hen bytte her, ikke bare lese. */}
            <div className="menu__group">
              <span className="menu__groupname">PRODUKSJON</span>
              <button type="button" className="menu__row" onClick={() => setPane("produksjon")}>
                <span>{production.name}</span>
                <span className="menu__rowvalue">
                  {/* Invitasjoner man har latt ligge. Den som valgte «bli
                      hvor jeg er» skal fortsatt kunne finne veien dit —
                      et varsel som bare vistes én gang er et varsel man
                      mister mens telefonen ligger i lomma. */}
                  {uleste === 0 ? null : (
                    <span className="menu__merke">{uleste}</span>
                  )}
                  {" "}›
                </span>
              </button>
              {/* Hva du ER paa produksjonen. Det er ogsaa det som avgjoer
                  hva du faar skrive — og for teknikeren: om jam-knappen
                  finnes. Se state/fagfunksjon.ts. */}
              <button type="button" className="menu__row" onClick={() => setPane("fagfunksjon")}>
                <span>Fagfunksjon og team</span>
                <span className="menu__rowvalue">›</span>
              </button>
              <span className="menu__readonly">
                Opptaksdag {shootDay}
                {unitName === "" ? "" : ` · ${unitName}`} ·{" "}
                {production.framerate} fps
                {/*
                  * «Jammet 06:12, 9 t siden» hoerer hit og ikke i toppstripa.
                  *
                  * Det er informasjonen regi kan handle PAA — men den stilles
                  * sjelden, og den sto paa den ene linja som alltid er synlig.
                  * Under tidskoden staar naa produksjonen, fordi det er lett
                  * aa begynne aa logge privat naar man bytter regelmessig.
                  *
                  * Regnet ved gjengiving og ikke paa et tikk: timetallet
                  * endrer seg én gang i timen, og menyen staar sjelden saa
                  * lenge oppe.
                  */}
                <>
                  <br />
                  {/* «Ikke jammet» er ikke en feil, men det er en
                      tilstand regi maa kjenne: tidskoden er ikke avtalt
                      med noen ennaa. Samme dempede farge som «gammel» —
                      begge betyr «den staar ikke paa en boks». */}
                  <span
                    className={jam.tilstand === "jammet"
                      ? undefined : "menu__jam--gammel"}
                  >
                    {jam.tekst}
                  </span>
                </>
              </span>
              {/*
                * Framerate kan BARE endres paa privat.
                *
                * En produksjons framerate settes av prodleder paa nettflata og
                * er en egenskap ved produksjonen — to regier paa samme
                * produksjon maa regne likt. Privat har ingen prodleder.
                */}
              {production.id === PRIVAT_ID ? (
                <button
                  type="button"
                  className="menu__row"
                  onClick={() => setPane("framerate")}
                >
                  <span>Framerate</span>
                  <span className="menu__rowvalue">{production.framerate} fps ›</span>
                </button>
              ) : null}
            </div>

            {/*
              * ========  STILLAS  ========
              *
              * Finnes BARE naar innlogging er av. Med auth paa sier
              * `production_member` hvem du er, og basen haandhever det —
              * da ville dette vaert en knapp som lyver.
              *
              * Den er her for én proeve: at et team deler logg kan bare
              * proeves med to enheter som er to ULIKE MEDLEMMER paa samme
              * team. Det krever ikke innlogging, bare at de er ulike.
              */}
            {/*
              * KONTO. Fantes ikke i appen i det hele tatt.
              *
              * `loggUt` laa i tilstanden og ble aldri kalt fra noen
              * flate, og adressen man var logget inn med sto ingen steder.
              * Brukeren 31.10.2026: «det er ikke noe "min konto" side i
              * menyen som gjør at jeg kan dobbeltsjekke at det er min mail
              * som er logget inn.» Det er et rimelig spoersmaal aa kunne
              * svare paa naar man lurer paa hvorfor en invitasjon ikke
              * dukket opp.
              */}
            {!krevInnlogging ? null : (
              <div className="menu__group">
                <span className="menu__groupname">KONTO</span>
                <span className="menu__row menu__readonly">
                  <span>Logget inn som</span>
                  <span className="menu__rowvalue">{epost === "" ? "…" : epost}</span>
                </span>
                <button
                  type="button"
                  className="menu__row"
                  onClick={() => { void loggUt(); }}
                >
                  <span>Logg ut</span>
                </button>
              </div>
            )}

            {krevInnlogging ? null : (
              <div className="menu__group">
                <span className="menu__groupname">MENS VI BYGGER</span>
                <button
                  type="button"
                  className="menu__row"
                  onClick={() => setPane("stillas")}
                >
                  <span>Hvem er denne telefonen</span>
                  <span className="menu__rowvalue">
                    {member.initials}{unitName === "" ? "" : ` · ${unitName}`} ›
                  </span>
                </button>
              </div>
            )}

            {/*
              * BYGGMERKET. Nederst, dempet, og ikke til aa trykke paa.
              *
              * Appen paa hjemskjermen bytter aldri versjon av seg selv, og
              * uten dette merket finnes det ingen vei til aa vite hvilken
              * versjon en telefon kjoerer — hverken for regi eller for den
              * som skal finne ut hvorfor noe ikke virker. Se
              * state/oppdatering.ts.
              */}
            {/*
              * UTBOKSEN, naar det staar noe i den.
              *
              * Null er den normale tilstanden, og da staar det ingenting —
              * et tall som alltid er null er stoey. Staar det noe, er det det
              * viktigste i menyen: loggen har ikke naadd redaktoeren.
              */}
            {iKoe === 0 && rapporter === 0 ? null : (
              <span className="menu__utboks">
                {/* «Venter paa nett» er en loegn naar serveren har svart
                    nei. Se loggsynk.avvist(). */}
                {rapporter === 0 ? null : (
                  <>
                    {rapporter === 1
                      ? "1 rapport venter på nett"
                      : `${rapporter} rapporter venter på nett`}
                    {iKoe === 0 ? null : <br />}
                  </>
                )}
                {iKoe === 0 ? null : nektet
                  ? `${iKoe} ${iKoe === 1 ? "loggrad" : "loggrader"} kom ikke fram`
                  : iKoe === 1
                    ? "1 loggrad venter på nett"
                    : `${iKoe} loggrader venter på nett`}
                {klage === "" ? null : (
                  <>
                    <br />
                    <span className="menu__utboks-klage">{klage}</span>
                  </>
                )}
                {/* RETTELSEN STAAR DER PROBLEMET STAAR.
                    Er raden avvist, kommer den ikke fram uansett hvor
                    lenge man venter — og da er det eneste man kan gjoere
                    aa bestemme seg for om den skal bli liggende. Knappen
                    laa bare i produksjonslista, og det er ett sted for
                    langt unna beskjeden om at noe er galt. */}
                {!nektet ? null : (
                  <>
                    <br />
                    <button
                      type="button"
                      className="menu__utboksknapp"
                      onClick={() => {
                        if (!kast) { setKast(true); return; }
                        setKast(false);
                        glemLokalProduksjon(production.id);
                        onClose();
                      }}
                    >
                      {kast
                        ? "Slett alt fra denne produksjonen? Kan ikke angres"
                        : "Fjern fra telefonen"}
                    </button>
                  </>
                )}
              </span>
            )}

            <span className="menu__bygg">Bygd {__BYGG__}</span>
          </div>
        ) : (
          <div className="menu__pane menu__pane--sub">
            <button type="button" className="menu__back" onClick={() => setPane("root")}>
              <span style={{ fontSize: 17, color: "var(--ink-dim)" }}>←</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)" }}>
                {PANE_NAVN[pane]}
              </span>
            </button>
            {pane === "sider" ? <PagesPane />
              : pane === "produksjon" ? <ProduksjonPane />
                : pane === "framerate" ? <FrameratePane />
                : pane === "fagfunksjon" ? <FagfunksjonPane />
                  : pane === "stillas" ? <StillasPane />
                  : <AvsluttPane onBackToLog={close} />}
          </div>
        )}
      </div>
    </div>
  );
});
